import { useCallback, useEffect, useState } from 'react';

// Gating hook for a server-persisted feature callout. Consumers supply:
//   - calloutKey: a stable, whitelisted key understood by the backend
//     (see ALLOWED_CALLOUT_KEYS in Api::V1::UsersController).
//   - context: the auth context (must expose getCurrentUser + markCalloutSeen).
//   - eligible: boolean indicating whether the callout's preconditions are
//     met (correct page, feature flag, role, etc.).
//   - blocked: boolean that is true while a higher-priority overlay is
//     covering the page (privacy gate, compliance modal, welcome modal, etc.).
//     The callout will wait until blocked flips back to false.
//   - reshowAfterMs: optional. When omitted the callout is one-time — once
//     dismissed it never returns. When set, dismissing only snoozes the
//     callout: it reappears after reshowAfterMs has elapsed (the server-side
//     seen_callouts timestamp is refreshed on every dismissal, so the snooze
//     window survives reloads and other devices). Consumers are expected to
//     flip `eligible` off once the callout is permanently satisfied (e.g. the
//     patient completed their first call).
//
// Returns { shouldShow, dismiss }. `shouldShow` is true only when the user
// is eligible, no blocking overlay is up, and the callout is not dismissed
// (one-time mode) or snoozed (recurring mode). Calling `dismiss` records the
// callout as seen (locally + server) so the overlay unmounts immediately
// while the API call persists in the background.
export const useFeatureCallout = ({ calloutKey, context, eligible, blocked = false, reshowAfterMs = null } = {}) => {
  const user = context?.auth?.getCurrentUser ? context.auth.getCurrentUser() : null;
  const serverSeenAtIso = user?.seen_callouts?.[calloutKey] || null;
  const alreadySeenOnServer = !!serverSeenAtIso;
  const recurring = Number.isFinite(reshowAfterMs) && reshowAfterMs > 0;

  const [dismissedLocally, setDismissedLocally] = useState(alreadySeenOnServer);
  const [locallyDismissedAtMs, setLocallyDismissedAtMs] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Keep the local "dismissed" flag in lockstep with the server-side flag in
  // both directions. Without this, an admin/QA flow that clears the seen
  // entry server-side cannot resurface the callout in an existing browser
  // session because the local true value would latch.
  useEffect(() => {
    setDismissedLocally(alreadySeenOnServer);
  }, [alreadySeenOnServer]);

  // Recurring mode: the callout is snoozed for reshowAfterMs after the most
  // recent dismissal, whether that dismissal happened in this session or was
  // persisted server-side (possibly from another device or page load).
  const serverSeenAtMs = serverSeenAtIso ? new Date(serverSeenAtIso).getTime() : null;
  const lastDismissedAtMs = Math.max(serverSeenAtMs || 0, locallyDismissedAtMs || 0) || null;
  const snoozedUntilMs = recurring && lastDismissedAtMs ? lastDismissedAtMs + reshowAfterMs : null;
  const snoozed = !!snoozedUntilMs && nowMs < snoozedUntilMs;

  // Re-render once the snooze window elapses so the callout can reappear
  // without the user navigating or refetching anything.
  useEffect(() => {
    if (!snoozed) return undefined;
    const timer = setTimeout(() => setNowMs(Date.now()), snoozedUntilMs - Date.now());
    return () => clearTimeout(timer);
  }, [snoozed, snoozedUntilMs]);

  const dismiss = useCallback(() => {
    if (!calloutKey) return;
    if (recurring) {
      setNowMs(Date.now());
      setLocallyDismissedAtMs(Date.now());
      if (context?.auth?.markCalloutSeen) {
        context.auth.markCalloutSeen(calloutKey);
      }
      return;
    }
    if (dismissedLocally) return;
    setDismissedLocally(true);
    if (context?.auth?.markCalloutSeen) {
      context.auth.markCalloutSeen(calloutKey);
    }
  }, [calloutKey, context, dismissedLocally, recurring]);

  const hiddenByDismissal = recurring ? snoozed : dismissedLocally || alreadySeenOnServer;
  const shouldShow = !!eligible && !blocked && !hiddenByDismissal;

  return { shouldShow, dismiss };
};

export default useFeatureCallout;
